import React, { useEffect, useState } from "react"
import styles from "./Login.module.scss"
import axios from "axios"
import { API_ROUTE, toasted } from "../../utils"
import toast from "react-hot-toast"
import { UserInterface } from "../../App"
import logo from "./logo-square.png"

interface LoginInterface {
    entryType: "login" | "register"
    setEntryType: React.Dispatch<React.SetStateAction<"login" | "register">>
    setUser: React.Dispatch<React.SetStateAction<UserInterface>>
}

const Login: React.FC<LoginInterface> = ({ entryType, setEntryType, setUser }) => {
    const [loading, setLoading] = useState(false)
    const [buttonMessage, setButtonMessage] = useState("Continue")

    async function submit() {
        if (loading) return
        const email = (document.querySelector("input[name=email]") as HTMLInputElement).value
        const password = (document.querySelector("input[name=password]") as HTMLInputElement).value

        async function register({
            name,
            email,
            password,
        }: {
            name: string
            email: string
            password: string
        }) {
            try {
                setLoading(true)

                setTimeout(() => {
                    setButtonMessage("We're getting everything setup for you...")
                }, 4000)

                const response = await axios.post(API_ROUTE + "/auth/register", {
                    name,
                    email,
                    password,
                })

                setUser(response.data)
            } catch (e) {
                setLoading(false)
                const error = (e as any)?.response?.data?.message || (e as any).message
                toast.error(error, toasted())
            }
        }

        async function login(email: string, password: string) {
            try {
                setLoading(true)
                const response = await axios.post(API_ROUTE + "/auth/verify-user", {
                    email,
                    password,
                })

                setButtonMessage("Welcome Back! We're getting everything ready...")
                setUser(response.data)
            } catch (e) {
                setLoading(false)
                const error = (e as any)?.response?.data?.message || (e as any).message
                toast.error(error, toasted())
            }
        }

        switch (entryType) {
            case "register":
                const name = (document.querySelector("input[name=name]") as HTMLInputElement).value

                if (!name || !email || !password) {
                    toast.error("Please fill out all fields", toasted("signup"))
                    return
                }

                register({ name, email, password })
                break
            case "login":
                if (!email || !password) {
                    toast.error("Please fill out all fields", toasted("login"))
                    return
                }

                login(email, password)
                break
        }
    }

    useEffect(() => {
        function handleEnterKey(e: KeyboardEvent) {
            if (e.key === "Enter") submit()
        }

        document.addEventListener("keyup", handleEnterKey)
        return () => document.removeEventListener("keyup", handleEnterKey)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    async function continueAsGuest() {
        try {
            setLoading(true)

            setTimeout(() => {
                setButtonMessage("Please wait while we get everything ready for you...")

                setTimeout(() => {
                    setButtonMessage("Almost there!...")
                }, 8000)
            }, 4000)

            const response = await axios.post(API_ROUTE + "/auth/register", {
                name: "Guest",
                email: "guest_" + Math.random().toString(36).substring(2, 15) + "@pretzel.space",
                password: Math.random().toString(36).substring(2, 15),
            })

            setUser(response.data)
        } catch (e) {
            setLoading(false)
            const error = (e as any)?.response?.data?.message || (e as any).message
            toast.error(error, toasted())
        }
    }

    return (
        <div className={styles.Login}>
            <div className={styles.header}>
                <img src={logo} height={70} alt="" />
                <h1>Pretzel</h1>
            </div>
            <br /> <br />
            <h2>{entryType === "register" ? "Create your account" : "Login to your account"}</h2>
            <br />
            {entryType === "register" && (
                <div className="flex">
                    <i className="material-symbols-rounded">account_box</i>
                    <input type="text" name="name" placeholder="Full Name" />
                </div>
            )}
            <div className="flex">
                <i className="material-symbols-rounded">alternate_email</i>
                <input type="email" name="email" placeholder="email@address.com" />
            </div>
            <div className="flex">
                <i className="material-symbols-rounded">password</i>
                <input type="password" name="password" placeholder="Password" />
            </div>
            <button
                disabled={loading}
                onClick={() => {
                    if (!loading) submit()
                }}>
                {loading ? (
                    <div className="loader"></div>
                ) : (
                    <i className="material-symbols-rounded">login</i>
                )}
                {buttonMessage}
            </button>
            <span
                className={styles.link}
                onClick={() => {
                    if (!loading) setEntryType(entryType === "login" ? "register" : "login")
                }}>
                {entryType === "login"
                    ? "Don't have an account? Sign up"
                    : "Already have an account? Login"}
            </span>
            <span onClick={continueAsGuest} className={styles.link}>
                Continue as Guest
            </span>
        </div>
    )
}

export default Login
